import { Input } from "components/common/Input/Input";
import { useSize } from "./useSize";
import { Unit } from "store/types/unit";
import { useToggle } from "client/hooks/utils/useToggle";
import { Popup } from "components/common/Popup/Popup";
import { ArrowIcon } from "components/common/Icons/ArrowIcon";
import { List } from "components/common/List/List";
import { ReactNode } from "react";
import { Translate } from "components/common/Translate/Translate";
import { translateFunc } from "client/utils/translateFunc";
import { Checkbox } from "components/common/Checkbox/Checkbox";
import { capitalize } from "client/utils/capitalize";
import { DPI } from "../DPI/DPI";
import { roundDimension } from "store/features/resizeEditor/utils/roundDimension";

export const Size = () => {
    const {
        height,
        width,
        handleHeightChange,
        handleWidthChange,
        isLocked,
        toggleLock,
        unit,
        widthValid,
        heightValid,
        dpi,
        maximumDimension,
        handleUnitChange,
    } = useSize();

    return (
        <div className="flex flex-col gap-4">
            <div className="flex max-w-full items-end gap-2">
                <div className="flex flex-col min-w-0 gap-2">
                    <div className="capitalize pb-2" data-testid="resize-settings-by-dimensions-width-label">
                        <Translate keyName="width" />
                    </div>
                    <Input
                        type="number"
                        acceptFloat={unit !== "pixels"}
                        value={roundDimension(unit, width)}
                        min={0}
                        onChange={handleWidthChange}
                        data-testid="resize-settings-by-dimensions-width"
                    />
                </div>
                <div className="flex flex-col min-w-0 gap-2 justify-between h-full">
                    <div
                        className="flex items-center justify-between"
                        data-testid="resize-settings-by-dimensions-height-label"
                    >
                        <div className="capitalize">
                            <Translate keyName="height" />
                        </div>
                        <UnitDropDown unit={unit} handleChange={handleUnitChange} />
                    </div>
                    <Input
                        type="number"
                        acceptFloat={unit !== "pixels"}
                        value={roundDimension(unit, height)}
                        min={0}
                        onChange={handleHeightChange}
                        data-testid="resize-settings-by-dimensions-height"
                    />
                </div>
            </div>
            {(!widthValid || !heightValid) && (
                <div className="text-validation-error" data-testid="resize-settings-by-dimensions-error">
                    {`Maximum allowed value for width / height is ${maximumDimension} ${unit} ${
                        unit !== "pixels" ? `at ${dpi} DPI` : ""
                    }`}
                </div>
            )}
            {unit !== "pixels" && <DPI />}

            <div className="flex items-center justify-between mt-2">
                <Checkbox
                    defaultIsChecked={isLocked}
                    handleChange={toggleLock}
                    label={capitalize(translateFunc("lock-aspect-ratio"))}
                    testId="resize-settings-by-dimensions-lock"
                />
            </div>
        </div>
    );
};

interface UnitDropDownProps {
    unit: Unit;
    handleChange: (unit: Unit) => void;
}

export const UnitDropDown = ({ unit, handleChange }: UnitDropDownProps) => {
    const { isOpen, toggle, onClose: close } = useToggle();

    const handleClick = (unit: Unit) => {
        handleChange(unit);
        close();
    };

    const unitShortValues: Record<Unit, string> = {
        pixels: "px",
        cm: "cm",
        inch: "in",
        mm: "mm",
    };

    return (
        <div>
            <Popup
                isOpen={isOpen}
                close={close}
                content={
                    <div
                        className="absolute flex flex-col top-full right-0 bg-white dark:bg-darkSurface-200 rounded-xl shadow-lg border border-irGray-400 dark:border-darkSurface-600 overflow-hidden"
                        data-testid="add-images-content"
                    >
                        <List
                            callback={handleClick}
                            items={
                                {
                                    pixels: (
                                        <UnitDropDownListItem
                                            isSelected={unit === "pixels"}
                                            unit={"pixels"}
                                            handleClick={handleClick}
                                        />
                                    ),
                                    inch: (
                                        <UnitDropDownListItem
                                            isSelected={unit === "inch"}
                                            unit={"inch"}
                                            handleClick={handleClick}
                                        />
                                    ),
                                    cm: (
                                        <UnitDropDownListItem
                                            isSelected={unit === "cm"}
                                            unit={"cm"}
                                            handleClick={handleClick}
                                        />
                                    ),
                                    mm: (
                                        <UnitDropDownListItem
                                            isSelected={unit === "mm"}
                                            unit={"mm"}
                                            handleClick={handleClick}
                                        />
                                    ),
                                } as Record<Unit, ReactNode>
                            }
                        />
                    </div>
                }
            >
                <div
                    className="flex items-center gap-1 cursor-pointer"
                    onClick={toggle}
                    data-testid={`resize_settings_unit`}
                >
                    <div>{unitShortValues[unit]}</div>
                    <div className="mt-1">
                        <ArrowIcon />
                    </div>
                </div>
            </Popup>
        </div>
    );
};

interface UnitDropDownListItemProps {
    unit: Unit;
    isSelected: boolean;
    handleClick: (unit: Unit) => void;
}

const UnitDropDownListItem = ({ unit, isSelected, handleClick }: UnitDropDownListItemProps) => {
    const unitValues: Record<Unit, string> = {
        pixels: translateFunc("pixel"),
        cm: translateFunc("centimeter"),
        inch: translateFunc("inch"),
        mm: translateFunc("millimeter"),
    };

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                handleClick(unit);
            }}
            className={`py-2 px-5 ${
                isSelected
                    ? "bg-irBlue-200 dark:bg-darkSurface-100"
                    : "hover:bg-irBlue-100 dark:hover:bg-darkSurface-300"
            } ${isSelected && "text-white"} font-normal`}
            data-testid={`resize_settings_unit-option-${unit}`}
        >
            {unitValues[unit]}
        </div>
    );
};
